/* eslint-disable react/display-name */
import React, { useState } from 'react'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import { getConfigByKey, numberWithComma, roundNumber } from 'helpers/format'
import { Col, Dropdown, Image, Row, Space, Tag } from 'antd'
import { appConfig } from 'config'
import { TableListPage } from 'Components/common/Table'
import { DeleteOutlined, PrinterOutlined } from '@ant-design/icons'
import { DocumentConfigKey, DocumentDefaultTitle, ImageFallback } from 'helpers/constanst'
import { selectPosBill } from 'Store/orders/selector'
import RootState from 'typings/RootState'
import { connect } from 'react-redux'
import { ActionButton, PrimaryButton } from 'Components/button'
import { AppInput, MenuPrints } from 'Components/form'
import { SupplierOrderData } from 'Store/supplier-order/types'
import { QuotationProduct } from 'Store/quotation/types'
import { OverstockTag, ReturnTag } from 'Components/utilities'
import { selectConfig, selectConfigList } from 'Store/pos/selector'
import ProductUomSystemInfo from 'Pages/Product/components/ProductUomSystemInfo'

type Props = {
  products: QuotationProduct[]
  onUpdateData?: (products: QuotationProduct[]) => void
  viewMode?: boolean
  saleOrderPdfRef?: any
  saleInvoicePdfRef?: any
  saleOrderLitePdfRef?: any
  quotation_no?: string
  detail?: SupplierOrderData
  ignoreReturn?: boolean
  onSelectTitle: (title: string) => void
} & ReduxProps

const ProductsTable: React.FunctionComponent<Props> = props => {
  const {
    products,
    onUpdateData = () => {},
    viewMode = false,
    saleOrderPdfRef,
    saleOrderLitePdfRef,
    detail,
    ignoreReturn = false,
    configList,
    onSelectTitle,
  } = props
  const configData = getConfigByKey(configList, DocumentConfigKey.DELIVERY)
  const config = configData && configData?.config_value && JSON.parse(configData?.config_value || '')

  const renderInfo = (val: any) => {
    return <RowTile>{val}</RowTile>
  }

  const onUpdateQuantity = (product: QuotationProduct, val: string) => {
    const newProduct = products.map(item => {
      if (item.product_id === product.product_id && item.product_option_id === product.product_option_id) {
        let quantity = parseInt(val)
        if (quantity > product.current_stock) {
          quantity = product.current_stock
        }
        item.quantity = quantity
      }
      return item
    })
    onUpdateData(newProduct)
  }

  const onUpdatePrice = (product: QuotationProduct, val: string) => {
    const newProduct =
      products &&
      products.map(item => {
        if (item.product_id === product.product_id && item.product_option_id === product.product_option_id) {
          item.price = parseFloat(val)
        }
        return item
      })
    onUpdateData(newProduct)
  }

  const onRemoveProduct = (product: QuotationProduct) => {
    const newProduct =
      products &&
      products.filter(
        item => item.product_id != product.product_id || item.product_option_id != product.product_option_id,
      )
    onUpdateData(newProduct)
  }

  const renderImage = (photo: any) => (
    <ThumbImage src={appConfig.assetCdnEndpoint + photo} fallback={ImageFallback} />
  )

  const renderProductName = (item: string, record: QuotationProduct) => {
    const isSameTitle = record.title == record.product_option_title
    return (
      <>
        <RowTile>{record.title}</RowTile>
        {!isSameTitle && <ProductDesc>{record.product_option_title}</ProductDesc>}
        <Space direction="vertical" size={'small'}>
          {record.sale_item_type === 'overstock' && (
            <OverstockTag isOverstock={record.sale_item_type === 'overstock'} />
          )}
          <ReturnTag
            isReturn={record?.return_qty > 0}
            returnQty={record?.return_qty}
            returnUom={record?.product_uom_system || ''}
          />
        </Space>
      </>
    )
  }

  const renderActionButton = (text: string, record: any) => (
    <Space size="middle">
      <ActionButton
        text=""
        icon={<DeleteOutlined />}
        label="ลบ"
        action="view"
        onClick={() => onRemoveProduct(record)}
      />
    </Space>
  )

  const renderQuantity = (item: string, record: QuotationProduct) => {
    if (viewMode) {
      return (
        <Space>
          {numberWithComma(record.quantity)} {renderProductUom(record.quantity, record)}
        </Space>
      )
    } else {
      return (
        <AppInput
          name="quantity"
          label=""
          type="number"
          errorMsg="จำนวน"
          isRequired={true}
          isError={isNaN(record.quantity) || record.quantity <= 0}
          value={record.quantity}
          onChange={val => onUpdateQuantity(record, val)}
          key={`${record.product_id}-${record.product_option_id}`}
        />
      )
    }
  }

  const renderPrice = (item: string, record: QuotationProduct) => {
    if (viewMode) {
      return numberWithComma(record.price_before_discount, true)
    } else {
      return (
        <AppInput
          name="price_before_discount"
          label=""
          type="number"
          errorMsg="ราคา"
          isRequired={true}
          isError={isNaN(record.price_before_discount) || record.price_before_discount <= 0}
          value={roundNumber(record.price_before_discount)}
          onChange={val => onUpdatePrice(record, val)}
          key={`${record.product_id}-${record.product_option_id}}`}
        />
      )
    }
  }

  const renderDiscountPrice = (item: string, record: QuotationProduct) => {
    if (viewMode) {
      return numberWithComma(record.discount, true)
    } else {
      return (
        <AppInput
          name="discount"
          label=""
          type="number"
          errorMsg="ราคา"
          isRequired={true}
          isError={false}
          value={roundNumber(record.discount)}
          onChange={val => {}}
          key={`${record.product_id}-${record.product_option_id}`}
        />
      )
    }
  }

  const renderTotalPrice = (item: string, record: QuotationProduct) => {
    return <TextRight>{numberWithComma((record.quantity || 0) * (record.price || 0), true)}</TextRight>
  }

  const renderStock = (quantity: number) => {
    if (viewMode) return
    return quantity <= 10 ? <StockMin>{numberWithComma(quantity)}</StockMin> : numberWithComma(quantity)
  }

  const renderProductUom = (quantity: number, record: QuotationProduct) => {
    return (
      <ProductUomSystemInfo
        multiplier={record.multiplier}
        quantity={record.quantity}
        uom={record.product_uom}
        quantitySystem={record.quantity_system}
        uomSystem={record.product_uom_system}
        showQuantity={true}
      />
    )
  }

  const columns = [
    {
      title: <ColTile>ลำดับ</ColTile>,
      dataIndex: 'no',
      key: 'no',
      render: (value: any, record: any, index: number) => {
        return renderInfo(index + 1)
      },
      width: '50px',
    },
    {
      title: <ColTile>รูปภาพ</ColTile>,
      dataIndex: 'picture',
      key: 'picture',
      width: '80px',
      render: renderImage,
    },
    {
      title: <ColTile>รหัสสินค้า</ColTile>,
      dataIndex: 'code',
      key: 'code',
      width: '80px',
      render: (value: any) => renderInfo(value || '-'),
    },
    {
      title: <ColTile>รายการ</ColTile>,
      dataIndex: 'title',
      key: 'title',
      render: renderProductName,
      width: '150px',
    },
    {
      title: <ColTile>ยี่ห้อ</ColTile>,
      dataIndex: 'brand',
      key: 'brand',
      render: (value: any) => renderInfo(value || '-'),
    },
    {
      title: viewMode ? '' : <ColTile>สต๊อกคงเหลือ</ColTile>,
      dataIndex: 'current_stock',
      key: 'current_stock',
      width: viewMode ? '0px' : '150px',
      render: renderStock,
    },
    {
      title: <ColTile>จำนวน</ColTile>,
      dataIndex: 'quantity',
      key: 'quantity',
      width: '150px',
      render: renderQuantity,
    },
    // {
    //   title: <ColTile>หน่วย</ColTile>,
    //   dataIndex: 'unit',
    //   key: 'unit',
    //   render: renderProductUom,
    // },
    {
      title: <ColTile>ราคา/หน่วย</ColTile>,
      dataIndex: 'price',
      key: 'price',
      render: renderPrice,
    },
    {
      title: <ColTile>ส่วนลด/หน่วย</ColTile>,
      dataIndex: 'price',
      key: 'price',
      render: renderDiscountPrice,
    },
    {
      title: (
        <ColTile>
          <TextRight>ราคารวม</TextRight>
        </ColTile>
      ),
      dataIndex: 'total_discount',
      key: 'total_discount',
      render: renderTotalPrice,
    },
    {
      title: '',
      key: 'action',
      width: '50px',
      render: !viewMode && renderActionButton,
    },
  ]

  const renderHeader = () => {
    if (viewMode) {
      return (
        <Row justify="space-between" gutter={[32, 32]} align="middle">
          <Col>
            <BoxTitle>รายการสินค้า</BoxTitle>
          </Col>
          <Col>
            <Space>
              {saleOrderPdfRef && (
                <Dropdown
                  key="more"
                  overlay={
                    <MenuPrints
                      nomalPrintRef={saleOrderPdfRef}
                      litePrintRef={saleOrderLitePdfRef}
                      title={`ใบส่งสินค้า-${detail?.sale_order_no}`}
                      titleSelection={config?.header || null}
                      onSelectTitle={onSelectTitle}
                    />
                  }
                  trigger={['click', 'hover']}
                >
                  <PrimaryButton
                    btnType="Approve"
                    text="ใบส่งสินค้า"
                    icon={<PrinterOutlined />}
                    onClick={() => {}}
                    size="middle"
                  />
                </Dropdown>
              )}

              {/* {saleInvoicePdfRef && (
                <ReactToPrint
                  trigger={() => {
                    return (
                      <PrimaryButton
                        btnType="Primary"
                        text="ใบเสร็จรับเงิน"
                        icon={<PrinterOutlined />}
                        onClick={() => {}}
                        size="middle"
                      />
                    )
                  }}
                  content={() => saleInvoicePdfRef.current}
                  documentTitle={`ใบเสร็จรับเงิน-${detail?.sale_order_no}`}
                />
              )} */}
            </Space>
          </Col>
        </Row>
      )
    } else {
      return (
        <Row justify="space-between" gutter={[32, 32]} align="middle">
          <Col>
            <BoxTitle>
              เลือกสินค้าสำหรับออกใบเสนอราคา (จำนวนสินค้าที่เลือก {products?.length || 0} รายการ)
            </BoxTitle>
          </Col>
        </Row>
      )
    }
  }

  const filterProducts = (products: QuotationProduct[]) => {
    if (ignoreReturn) {
      return products && products?.filter(item => item?.return_qty < item?.quantity)
    } else {
      return products
    }
  }

  return (
    <BoxWrapper>
      <Row gutter={[16, 8]}>
        <Col span={24}>
          {renderHeader()}
          <TableListPage
            rowKey="product_option_id"
            tableName="product-purchase"
            columns={columns}
            datas={filterProducts(products)}
            size={'small'}
            scrollX={800}
            // rowClassName={(record: QuotationProduct) =>
            //   record?.return_qty >= record?.quantity ? 'row-disabled' : ''
            // }
          />
        </Col>
      </Row>
    </BoxWrapper>
  )
}

const BoxTitle = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body3};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.primary};
`

const StockMin = styled.div`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.error};
`

const BoxWrapper = styled.div`
  width: 100%;
  border-radius: 10px;
  padding: 15px 10px;
  //border: 1px solid ${(props: { theme: MyTheme }): string => props.theme.colors.whitebg};
  border: 1px solid #f0f0f0;
  background-color:  ${(props: { theme: MyTheme }): string => props.theme.colors.white};
`

const ThumbImage = styled(Image)`
  border-radius: 5px;
  width: 70px;
  height: 70px;
  object-fit: cover;
`

const ColTile = styled.span`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
`

const RowTile = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
`

const ProductDesc = styled(RowTile)`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.grey};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
`

const TextRight = styled.div`
  text-align: right;
`

const mapStateToProps = (state: RootState) => {
  return {
    billData: selectPosBill(state),
    configList: selectConfigList(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(ProductsTable)
